import type { ObjectivePriority } from './objective-proposals.ts';

export interface WorkspaceObjective {
  id: string;
  tenant_id: string;
  scope: 'company' | 'individual';
  owner_user_id: string | null;
  created_by: string;
  title: string;
  success_criteria: string;
  due_date: string | null;
  status: 'active' | 'achieved' | 'archived';
  /** 13/10/2026: how critical it is, the figure aimed at, the starting point. */
  priority: ObjectivePriority;
  target: string | null;
  baseline: string | null;
  /** A lever: the main objective it produces; null for a main objective. */
  parent_objective_id: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}
export type WorkspaceObjectiveInput =
  & Pick<WorkspaceObjective, 'scope' | 'owner_user_id' | 'title' | 'success_criteria' | 'due_date'>
  & Partial<Pick<WorkspaceObjective, 'priority' | 'target' | 'baseline' | 'parent_objective_id'>>;
/** J3: optional measure of an objective, kept apart from it. Its source
 * (15/10/2026): the records of one app list reaching one status, the HubSpot
 * deals entering one stage, or the HubSpot meetings of a type and/or outcome,
 * during the period [starts_on, due_date]. HubSpot labels are those of the
 * portal when the measure was chosen. */
export type WorkspaceObjectiveMeasureSource = 'app_status' | 'hubspot_deal_stage' | 'hubspot_meeting';
export type HubSpotMeetingOutcome = 'SCHEDULED' | 'COMPLETED' | 'RESCHEDULED' | 'NO_SHOW' | 'CANCELED';
export interface WorkspaceObjectiveMeasure {
  objective_id: string;
  tenant_id: string;
  source: WorkspaceObjectiveMeasureSource;
  collection_id: string | null;
  status_key: string | null;
  hubspot_portal_id: string | null;
  hubspot_pipeline_id: string | null;
  hubspot_pipeline_label: string | null;
  hubspot_stage_id: string | null;
  hubspot_stage_label: string | null;
  hubspot_meeting_type: string | null;
  hubspot_meeting_type_label: string | null;
  hubspot_meeting_outcome: HubSpotMeetingOutcome | null;
  target_value: number;
  starts_on: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}
/** Every source column is written, so that changing source clears the others. */
export type WorkspaceObjectiveMeasureInput = Pick<
  WorkspaceObjectiveMeasure,
  | 'source' | 'collection_id' | 'status_key' | 'hubspot_portal_id' | 'hubspot_pipeline_id'
  | 'hubspot_pipeline_label' | 'hubspot_stage_id' | 'hubspot_stage_label' | 'hubspot_meeting_type'
  | 'hubspot_meeting_type_label' | 'hubspot_meeting_outcome' | 'target_value' | 'starts_on'
>;

/** Count of a measured objective (read_objective_progress_j3). Counts are
 * null when the reader cannot see the source: withheld, never shown as zero. */
export interface WorkspaceObjectiveProgress {
  objectiveId: string;
  source: WorkspaceObjectiveMeasureSource;
  reason:
    | 'app_inaccessible'
    | 'status_missing'
    | 'hubspot_unavailable'
    | 'hubspot_pending'
    | 'hubspot_owner_unmapped'
    | null;
  listTitle: string | null;
  statusLabel: string | null;
  total: number | null;
  thisWeek: number | null;
}

export const canEditWorkspaceObjective = (
  objective: WorkspaceObjective,
  userId: string,
  isManager: boolean,
): boolean =>
  isManager ||
  (objective.scope === 'individual' && objective.owner_user_id === userId &&
    objective.created_by === userId);

export const workspaceObjectiveEvidenceRef = (
  objective: Pick<WorkspaceObjective, 'id' | 'version'>,
): string => `objective:${objective.id}:${objective.version}`;

/** Historical decisions used updated_at before objective versions were wired. */
export const workspaceObjectiveLegacyEvidenceRef = (
  objective: Pick<WorkspaceObjective, 'id' | 'updated_at'>,
): string => `objective:${objective.id}:${objective.updated_at}`;

export const hasCurrentWorkspaceObjectiveRef = (
  evidenceRefs: string[],
  objective: Pick<WorkspaceObjective, 'id' | 'version' | 'updated_at'>,
): boolean => evidenceRefs.includes(workspaceObjectiveEvidenceRef(objective)) ||
  evidenceRefs.includes(workspaceObjectiveLegacyEvidenceRef(objective));
